import React, { useMemo, useState } from 'react';
import './ConnectionsTab.css';
import '../../../styles/social-posts.css';
import SocialUserCard from '../../../components/social/SocialUserCard';
import { useChatDock } from '../../../contexts/ChatDockContext';
import { useNavigate } from 'react-router-dom';

const ConnectionsTab = ({
  connections = [],
  isOwnProfile,
  isLoading = false,
  onRemoveConnection,
  onViewProfile, // Optional
  onMessage,     // Optional
  onFindMatches, // New: trigger suggestions
  matchSuggestions = [],
  onConnectWithSuggestion, // New: connect from suggestions
  onRespondConnection, // New: accept/reject inbound
  respondingMap = {},
  findLoading = false
}) => {
  const [filter, setFilter] = useState('all'); // all | pending | accepted
  const { openChat } = useChatDock();
  const navigate = useNavigate();
  const [ownerMenuOpen, setOwnerMenuOpen] = useState({});

  const filteredConnections = useMemo(() => {
    if (filter === 'pending') return connections.filter(c => (c.status || '').toLowerCase() === 'pending');
    if (filter === 'accepted') return connections.filter(c => (c.status || '').toLowerCase() === 'accepted');
    return connections;
  }, [connections, filter]);

  return (
    <div className="profile-section">
      <h2>Your Connections</h2>
      <div className="connections-filter-row">
        <button
          type="button"
          className={`filter-chip ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button
          type="button"
          className={`filter-chip ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Pending
        </button>
        <button
          type="button"
          className={`filter-chip ${filter === 'accepted' ? 'active' : ''}`}
          onClick={() => setFilter('accepted')}
        >
          Accepted
        </button>
      </div>
      {isOwnProfile && onFindMatches && (
        <div className="suggestions-card" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0 }}>People you may connect with</h3>
            <button type="button" className="edit-btn" onClick={onFindMatches} disabled={findLoading}>
              {findLoading ? 'Finding…' : 'Find Matches'}
            </button>
          </div>
          {Array.isArray(matchSuggestions) && matchSuggestions.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10, marginTop: 10 }}>
              {matchSuggestions.map((m, idx) => (
                <div key={m.id || idx} className="card" style={{ padding: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <img src={m.avatar_url || '/default-avatar.png'} alt={m.display_name || m.name || 'User'} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <strong>{m.display_name || m.name || 'User'}</strong>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Speaks: {Array.isArray(m.native_languages) ? m.native_languages.join(', ') : (m.native_languages || 'N/A')}</span>
                    </div>
                  </div>
                  {onConnectWithSuggestion && (
                    <button type="button" className="edit-btn" style={{ marginTop: 8 }} onClick={() => onConnectWithSuggestion(m)}>
                      Connect
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ marginTop: 8, color: 'var(--text-secondary)' }}>No suggestions yet. Click Find Matches.</p>
          )}
        </div>
      )}
      {isLoading ? (
        <div className="connections-skeleton">
          <div className="skeleton-card shimmer" style={{ height: 64 }} />
          <div className="skeleton-card shimmer" style={{ height: 64, marginTop: 8 }} />
        </div>
      ) : filteredConnections.length === 0 ? (
        <p>No connections yet.</p>
      ) : (
        <div className="connections-grid user-grid">
          {filteredConnections.map((conn, idx) => {
            const id = conn.id || idx;
            const displayName = conn.displayName || conn.name || conn.email || 'User';
            const userData = {
              ...conn,
              id,
              display_name: displayName,
              avatar_url: conn.avatar_url || null,
            };
            const statusLabel = (conn.status || '').toLowerCase();
            const extraBadge = statusLabel === 'accepted'
              ? { label: 'Connected', title: 'Connection accepted' }
              : statusLabel === 'pending'
                ? { label: 'Pending', title: 'Connection pending' }
                : null;

            const handleViewProfile = () => {
              if (onViewProfile) onViewProfile(conn);
              else window.open(`/app/profile/${id}`, '_blank', 'noopener,noreferrer');
            };

            const handleMessage = () => {
              if (onMessage) onMessage(conn);
              else openChat(id);
            };

            return (
              <div key={id} className="connection-card-wrapper">
                {/* Status chip and kebab positioned inside card */}
                <div className="connection-card-top-row">
                  {statusLabel && (
                    <span className={`status-chip status-${statusLabel}`}>
                      {statusLabel}
                    </span>
                  )}
                  {isOwnProfile && onRemoveConnection && (
                    <div className="connection-kebab-wrap">
                      <button
                        type="button"
                        className="social-kebab-btn"
                        aria-label="Connection options"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOwnerMenuOpen(prev => ({
                            ...prev,
                            [id]: !prev[id]
                          }));
                        }}
                      >
                        ⋮
                      </button>
                      {ownerMenuOpen[id] && (
                        <div className="social-kebab-menu">
                          <button
                            type="button"
                            className="social-kebab-item"
                            onClick={() => {
                              handleViewProfile();
                              setOwnerMenuOpen(prev => ({ ...prev, [id]: false }));
                            }}
                          >
                            View profile
                          </button>
                          <div className="social-kebab-danger-label">Danger zone</div>
                          <button
                            type="button"
                            className="social-kebab-item danger"
                            onClick={() => {
                              const confirmed = window.confirm('Remove this connection? This action cannot be undone.');
                              if (!confirmed) return;
                              setOwnerMenuOpen(prev => ({ ...prev, [id]: false }));
                              onRemoveConnection(conn);
                            }}
                          >
                            Remove connection
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <SocialUserCard
                  user={userData}
                  isSelf={false}
                  canMessage
                  extraBadge={extraBadge}
                  onClick={handleViewProfile}
                  onMessage={handleMessage}
                  showHoverDetails
                />
                {/* Accept/Reject for inbound pending requests */}
                {isOwnProfile && conn.direction === 'inbound' && statusLabel === 'pending' && onRespondConnection && (
                  <div className="connection-respond-row">
                    <button
                      className="accept-connection-btn"
                      disabled={!!respondingMap[conn.connection_row_id]}
                      onClick={() => onRespondConnection(conn, 'accepted')}
                    >
                      {respondingMap[conn.connection_row_id] ? 'Accepting…' : 'Accept'}
                    </button>
                    <button
                      className="reject-connection-btn"
                      disabled={!!respondingMap[conn.connection_row_id]}
                      onClick={() => onRespondConnection(conn, 'rejected')}
                    >
                      {respondingMap[conn.connection_row_id] ? 'Rejecting…' : 'Reject'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConnectionsTab;